/**
 * @fileoverview Run Page
 * @description Scorecard for a single evaluation run.
 */

import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  ClipboardList,
  FileJson,
  Gauge,
  Play,
  Search,
  Sparkles,
  TrendingDown,
  X,
} from 'lucide-react';
import {
  EvaluationBreadcrumbs,
  MissingRecord,
  ScoreChip,
  SummaryMetric,
} from '../components/common';
import { AnalysisPanel } from '../components/AnalysisPanel';
import { ResultsTable } from '../components/ResultsTable';
import { ScenarioConversation } from '../components/ScenarioConversation';
import { runToReport } from '../utils/evaluationStore';
import { bandForScore } from '../utils/evaluationView';
import { aiEvaluationResultsApi } from '../services/aiEvaluationResultsApi';
import { BAND_COLORS, BAND_FILTERS, BAND_LABELS, BAND_STATUS } from '../constants';

/**
 * Render the scorecard for one stored run.
 * @param {Object} props - Workspace projects.
 * @returns {JSX.Element} Run page.
 */
export function RunPage({ projects }) {
  const { projectId, cycleId, scenarioId, runId } = useParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [bandFilter, setBandFilter] = useState('all');
  const [selectedRow, setSelectedRow] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const project = projects.find((item) => item.id === projectId);
  const cycle = project?.cycles.find((item) => item.id === cycleId);
  const scenario = cycle?.scenarios.find((item) => item.id === scenarioId);
  const run = scenario?.runs.find((item) => item.id === runId);

  const view = useMemo(() => (run ? runToReport(run) : null), [run]);

  const visibleRows = useMemo(() => {
    if (!view) {
      return [];
    }
    const term = searchTerm.trim().toLowerCase();
    return view.evaluators.filter((row) => {
      const matchesBand = bandFilter === 'all' || row.band === bandFilter;
      const haystack = `${row.report} ${row.field} ${row.evaluator} ${row.framework} ${row.approach}`.toLowerCase();
      return matchesBand && haystack.includes(term);
    });
  }, [bandFilter, searchTerm, view]);

  const distribution = useMemo(() => (view?.evaluators || []).reduce(
    (counts, row) => (row.band in counts ? { ...counts, [row.band]: counts[row.band] + 1 } : counts),
    { green: 0, amber: 0, red: 0 },
  ), [view]);

  if (!project || !scenario || !run) {
    return <MissingRecord icon={Play} message="This run no longer exists." />;
  }
  const isScenarioLayout = Boolean(view.scenarioLayout);
  const overallBand = bandForScore(view.summary.overallAverage);
  const scoredCount = distribution.green + distribution.amber + distribution.red;

  /**
   * Request a plain-language interpretation of this run.
   * @returns {Promise<void>} Resolves once the request settles.
   */
  const handleGenerateInsights = async () => {
    setIsAnalyzing(true);
    setErrorMessage('');
    try {
      setAnalysis(await aiEvaluationResultsApi.humanize({
        title: `${project.name} — ${scenario.name}`,
        summary: view.summary,
        evaluators: view.evaluators,
      }));
    } catch (requestError) {
      setErrorMessage(
        requestError.details?.error?.message
        || requestError.message
        || 'AI insights are unavailable right now.',
      );
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <>
      <EvaluationBreadcrumbs
        trail={[
          { label: project.name, to: `/ai-evaluation-results/${project.id}` },
          { label: cycle.name, to: `/ai-evaluation-results/${project.id}/${cycle.id}` },
          { label: scenario.name, to: `/ai-evaluation-results/${project.id}/${cycle.id}/${scenario.id}` },
          { label: run.label },
        ]}
      />

      <Paper className="aier-run-hero" elevation={0} sx={{ '--band-color': BAND_COLORS[overallBand] }}>
        <Box className="aier-run-hero-main">
          <Box className="aier-header-icon">
            <FileJson size={24} />
          </Box>
          <Box minWidth={0}>
            <Typography className="aier-eyebrow">Evaluation run</Typography>
            <Typography variant="h4" component="h1" className="aier-run-title">{run.label}</Typography>
            <Typography className="aier-run-hero-description">{view.description}</Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap className="aier-run-meta">
              {run.generatedAt && (
                <Chip size="small" variant="outlined" label={`Generated ${new Date(run.generatedAt).toLocaleString()}`} />
              )}
              <Chip size="small" variant="outlined" label={`Recorded ${new Date(run.importedAt).toLocaleString()}`} />
              {view.sources.map((source, index) => {
                const label = typeof source === 'string' ? source : source?.name || JSON.stringify(source);
                return <Chip key={`${label}-${index}`} size="small" variant="outlined" label={label} />;
              })}
            </Stack>
          </Box>
        </Box>

        <Box className="aier-run-hero-score">
          <Typography className="aier-hero-score-label">Overall score</Typography>
          <Typography className="aier-hero-score-value">{view.summary.overallAverage.toFixed(2)}</Typography>
          <Typography className="aier-hero-score-status">{BAND_STATUS[overallBand]}</Typography>
          <Box className="aier-hero-score-track">
            <Box
              className="aier-hero-score-fill"
              style={{ width: `${Math.min(100, Math.max(0, view.summary.overallAverage * 100))}%` }}
            />
          </Box>
          <Button
            fullWidth
            variant="contained"
            startIcon={isAnalyzing ? <CircularProgress size={15} color="inherit" /> : <Sparkles size={16} />}
            disabled={isAnalyzing}
            onClick={handleGenerateInsights}
          >
            {isAnalyzing ? 'Generating…' : 'AI Insights'}
          </Button>
        </Box>
      </Paper>

      {errorMessage && (
        <Alert severity="error" onClose={() => setErrorMessage('')}>{errorMessage}</Alert>
      )}

      <AnalysisPanel analysis={analysis} onClose={() => setAnalysis(null)} />

      <Box className="aier-summary-grid" aria-label="Run summary">
        <SummaryMetric
          label="Overall average"
          value={view.summary.overallAverage.toFixed(2)}
          caption={isScenarioLayout ? 'Mean of scenario scores' : 'Mean of scored evaluators'}
          icon={Gauge}
          color="#01426A"
        />
        <SummaryMetric
          label={isScenarioLayout ? 'Scenarios scored' : 'Evaluators scored'}
          value={view.summary.evaluatorsScored}
          caption={isScenarioLayout
            ? `${view.summary.passed} passed · ${view.summary.failed} failed`
            : `${view.summary.notRun} not run`}
          icon={ClipboardList}
          color="#0097A9"
        />
        <SummaryMetric
          label="Below target"
          value={view.summary.belowTarget}
          caption="Score below 0.75"
          icon={TrendingDown}
          color="#C81E3A"
        />
        <Paper className="aier-band-key" elevation={0}>
          <Typography className="aier-metric-label">Score distribution</Typography>
          <Box className="aier-distribution-track" role="img" aria-label={`${distribution.green} green, ${distribution.amber} amber, ${distribution.red} red`}>
            {scoredCount === 0
              ? <Box className="aier-distribution-empty" />
              : ['green', 'amber', 'red'].filter((band) => distribution[band] > 0).map((band) => (
                <Box
                  key={band}
                  className="aier-distribution-segment"
                  style={{ width: `${(distribution[band] / scoredCount) * 100}%`, backgroundColor: BAND_COLORS[band] }}
                />
              ))}
          </Box>
          <Stack spacing={0.5} mt={1}>
            {Object.entries(BAND_LABELS).map(([band, label]) => (
              <Stack key={band} direction="row" spacing={1} alignItems="center">
                <Box className="aier-band-dot" sx={{ backgroundColor: BAND_COLORS[band] }} />
                <Typography className="aier-band-text">{label}</Typography>
                <Typography className="aier-band-count">{distribution[band]}</Typography>
              </Stack>
            ))}
          </Stack>
        </Paper>
      </Box>

      <Paper className="aier-results" elevation={0}>
        <Box className="aier-toolbar">
          <Box>
            <Typography variant="h6">{isScenarioLayout ? 'Scenario results' : 'Evaluator results'}</Typography>
            <Typography className="aier-toolbar-count">
              Showing {visibleRows.length} of {view.evaluators.length} {isScenarioLayout ? 'scenarios' : 'evaluators'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField
              size="small"
              placeholder={isScenarioLayout ? 'Search scenarios' : 'Search evaluators'}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="aier-search"
              slotProps={{
                htmlInput: { 'aria-label': isScenarioLayout ? 'Search scenarios' : 'Search evaluators' },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={15} />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <ToggleButtonGroup
              exclusive
              size="small"
              value={bandFilter}
              onChange={(_event, nextFilter) => nextFilter && setBandFilter(nextFilter)}
              aria-label="Score band filter"
            >
              {BAND_FILTERS.map((filter) => (
                <ToggleButton key={filter.value} value={filter.value}>{filter.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
        </Box>

        <ResultsTable
          rows={visibleRows}
          isScenarioLayout={isScenarioLayout}
          onSelectRow={setSelectedRow}
        />
      </Paper>

      <Drawer
        anchor="right"
        open={Boolean(selectedRow)}
        onClose={() => setSelectedRow(null)}
        slotProps={{ paper: { className: 'aier-drawer' } }}
      >
        {selectedRow && (
          <Box role="region" aria-label="Result detail">
            <Box className="aier-drawer-header">
              <Box>
                <Typography className="aier-eyebrow">Report {selectedRow.report}</Typography>
                <Typography variant="h6">{selectedRow.field}</Typography>
              </Box>
              <IconButton size="small" onClick={() => setSelectedRow(null)} aria-label="Close detail">
                <X size={18} />
              </IconButton>
            </Box>

            <Box className="aier-drawer-score">
              <ScoreChip score={selectedRow.score} band={selectedRow.band} />
              <Box>
                <Typography className="aier-drawer-field">{selectedRow.evaluator}</Typography>
                <Typography className="aier-metric-caption">{BAND_STATUS[selectedRow.band]}</Typography>
              </Box>
            </Box>

            <Stack spacing={2} className="aier-drawer-details">
              {selectedRow.metrics && (
                <Box>
                  <Typography className="aier-metric-label">Score breakdown</Typography>
                  <Box className="aier-breakdown">
                    {[
                      ['Goal coverage', selectedRow.metrics.goalCoverage],
                      ['Response quality', selectedRow.metrics.responseQuality],
                      ['Efficiency', selectedRow.metrics.efficiency],
                    ].map(([label, value]) => (
                      <Box key={label} className="aier-breakdown-item">
                        <Typography className="aier-breakdown-value">{value.toFixed(2)}</Typography>
                        <Typography className="aier-metric-caption">{label}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
              <Box>
                <Typography className="aier-metric-label">Framework</Typography>
                <Typography className="aier-drawer-value">{selectedRow.framework}</Typography>
              </Box>
              <Box>
                <Typography className="aier-metric-label">Approach</Typography>
                <Typography className="aier-drawer-value">{selectedRow.approach}</Typography>
              </Box>
              {selectedRow.detail && (
                <Box>
                  <Typography className="aier-metric-label">Evaluator detail</Typography>
                  <Typography className="aier-drawer-value">{selectedRow.detail}</Typography>
                </Box>
              )}
              <ScenarioConversation conversation={view.conversations?.[selectedRow.report]} />
            </Stack>
          </Box>
        )}
      </Drawer>
    </>
  );
}

export default RunPage;
